import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, TextButton } from '@xing-com/button';
import type { InteractionTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { SEARCH_ALERTS_MODAL_TYPES } from '@xing-com/crate-jobs-constants';
import { isValidEmail } from '@xing-com/crate-jobs-helpers';
import type { PropTrackActionData } from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { Hyperlink } from '@xing-com/hyperlink';
import { IllustrationPremiumSpotBell } from '@xing-com/illustrations';
import { Headline } from '@xing-com/typography';

import * as S from './styles';
import { useLOContent } from './use-lo-content';

type Props = {
  type: string | null;
  onClose: () => void;
};

export const LOContent: React.FC<Props> = ({ type, onClose }) => {
  const { formatMessage } = useIntl();
  const { stringifiedQuery, createSearchAlert } = useLOContent();
  const { track } =
    useTracking<InteractionTrackingEvent<PropTrackActionData>>();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [typeState] = useState(type);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.currentTarget.value);
  };

  const handleOnCreateAlert = (
    event: React.FormEvent<HTMLFormElement>
  ): void => {
    event.preventDefault();

    track({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: 'jobs_search_alert_create_confirm',
      PropActionOrigin:
        typeState === SEARCH_ALERTS_MODAL_TYPES.REPEAT
          ? 'jobs_search_recent_searches'
          : 'jobs_bell_link',
    });

    if (isValidEmail(email)) {
      setError('');
      createSearchAlert(email, setError);
    } else {
      setError(formatMessage({ id: 'JOBS_SEARCH_ALERT_LO_MODAL_EMAIL_ERROR' }));
    }
  };

  return (
    <S.Form $isLoggedOut onSubmit={handleOnCreateAlert}>
      <IllustrationPremiumSpotBell size="large" />
      <Headline size="xlarge" noMargin>
        <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT" />
      </Headline>
      <S.Text>
        <FormattedMessage
          id={
            typeState === SEARCH_ALERTS_MODAL_TYPES.REPEAT
              ? 'JOBS_SEARCH_ALERT_SUGGESTION_MESSAGE'
              : 'JOBS_SEARCH_ALERT_LO_MODAL_SUBTITLE'
          }
          values={{ query: <strong>{stringifiedQuery}</strong> }}
        />
      </S.Text>
      <S.Input
        value={email}
        onChange={handleOnChange}
        label={formatMessage({
          id: 'JOBS_SEARCH_ALERT_LO_MODAL_EMAIL_PLACEHOLDER',
        })}
        // @ts-expect-error FIXME: SC6
        error={error}
        helperText={error}
      />
      <S.Text size="medium">
        <FormattedMessage
          id="JOBS_SEARCH_ALERT_LO_MODAL_LEGAL_TEXT"
          values={{
            a1: (chunks) => (
              // @ts-expect-error FIXME: SC6
              <Hyperlink href={ROUTES.terms} target="_blank" rel="noopener">
                {chunks}
              </Hyperlink>
            ),
            a2: (chunks) => (
              // @ts-expect-error FIXME: SC6
              <Hyperlink href={ROUTES.privacy} target="_blank" rel="noopener">
                {chunks}
              </Hyperlink>
            ),
          }}
        />
      </S.Text>
      <S.ButtonWrapper>
        <Button type="submit" variant="primary">
          <FormattedMessage id="JOBS_SEARCH_ALERT_LO_MODAL_BUTTON" />
        </Button>
        <TextButton type="button" onClick={onClose}>
          <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT_CANCEL" />
        </TextButton>
      </S.ButtonWrapper>
    </S.Form>
  );
};
